import { useEffect, useMemo, useState } from 'react';
import { FilterMultiSelect, Pagination, ProjectThumb, stageLabel } from '@/components/ui';
import { STAGE_FILTER_OPTIONS } from '@/lib/stageFilter';
import type { Stage } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import type { Level } from '@/api/types';
import LevelSegment from './LevelSegment';
import TruncatedName from './TruncatedName';

import styles from '../MemberPerms.module.css';

const GAME_PAGE_SIZE = 10;

// 보유 권한 열에 표시할 "저장하면 갖게 될 게임" 한 행.
export interface SelectedGame {
  projectId: string;
  prefix: string;
  displayName: string | null;
  iconUrl: string | null;
  os: string[];
  // 게임의 현재 Stage — 메타를 아직 못 받은 기존 권한 행은 null.
  stage: string | null;
  level: Level;
  // 기존 부여 대비 상태: 신규/역할 변경/회수 예정 또는 현재 유지.
  changeKind: 'grant' | 'update' | 'revoke' | 'current';
}

interface HeldGamesListProps {
  games: SelectedGame[];
  disabled: boolean;
  onSetLevel: (projectId: string, level: Level) => void;
  onRemove: (projectId: string) => void;
  onClearAdditions: () => void;
  onClearExisting: () => void;
  disabledMessage?: string;
}

// 게임별 권한의 오른쪽 열 — 저장되어 있거나 이번에 넣은 게임. 검색·페이지와 무관하게 전부 담는다.
export default function HeldGamesList({
  games,
  disabled,
  onSetLevel,
  onRemove,
  onClearAdditions,
  onClearExisting,
  disabledMessage,
}: HeldGamesListProps) {
  const [query, setQuery] = useState('');
  const [stages, setStages] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const additions = games.filter(game => game.changeKind === 'grant');
  const activeExisting = games.filter(game => game.changeKind === 'current' || game.changeKind === 'update');
  // 보유 목록도 Stage·검색으로 좁힌다(클라이언트). 빈 Stage 선택 = 전체.
  const filteredGames = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return games.filter(game => {
      if (stages.length && !(game.stage && stages.includes(game.stage))) return false;
      if (!normalized) return true;
      return `${game.displayName ?? ''} ${game.prefix} ${game.projectId}`.toLowerCase().includes(normalized);
    });
  }, [query, stages, games]);
  const totalPages = Math.max(1, Math.ceil(filteredGames.length / GAME_PAGE_SIZE));
  useEffect(() => { setPage(current => Math.min(current, totalPages)); }, [totalPages]);
  const currentPage = Math.min(page, totalPages);
  const visibleGames = filteredGames.slice((currentPage - 1) * GAME_PAGE_SIZE, currentPage * GAME_PAGE_SIZE);

  return (
    <section className={styles.permissionBrowser} aria-label="게임별 보유 권한"
      style={disabled && !disabledMessage ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
      <div className={styles.permissionGroupHead}>
        <strong>보유 권한</strong><span>{games.length}</span>
      </div>
      {disabledMessage && <div className={`${styles.memberRequiredNotice} ${styles.permissionRequiredNotice}`} role="status">
        <NavIcon name="lock" size={14} />
        <span>{disabledMessage}</span>
      </div>}
      {/* 툴바: Stage 필터 · 검색 — 권한 추가 열과 같은 구성 */}
      <div className={styles.listToolbar}>
        <FilterMultiSelect label="Stage" values={stages} options={STAGE_FILTER_OPTIONS}
          onChange={values => { setStages(values); setPage(1); }} />
        <label className={`${styles.toolbarSearch} ${disabled ? styles.permissionSearchDisabled : ''}`}>
          <NavIcon name="search" size={14} />
          <input value={query} onChange={event => { setQuery(event.target.value); setPage(1); }}
            disabled={disabled}
            placeholder="보유 권한 검색 (이름 · prefix · id)" aria-label="현재 권한 검색" />
        </label>
      </div>
      {/* 일괄 동작 행 — 권한 추가 열의 '전체 추가' 행과 같은 모양. 추가 취소(미저장분) · 전체 회수(저장분) */}
      <div className={styles.bulkRow} data-tour="game-held-bulk">
        <span className={styles.bulkIcon} aria-hidden="true"><NavIcon name="grid" size={16} /></span>
        <div className={styles.bulkText}>
          <strong>일괄 회수</strong>
          <span>추가 중 {additions.length}개 · 보유 {activeExisting.length}개</span>
        </div>
        <div className={styles.gameRight}>
          <button type="button" className={styles.toolbarBtn} onClick={onClearAdditions}
            disabled={disabled || additions.length === 0}
            title="아직 저장하지 않은 추가를 모두 취소하고 왼쪽 목록으로 되돌리기">
            권한 추가 취소 ({additions.length})
          </button>
          <button type="button" className={`${styles.toolbarBtn} ${styles.toolbarBtnDanger}`} onClick={onClearExisting}
            disabled={disabled || activeExisting.length === 0}
            title="저장된 게임 권한을 모두 회수 대상으로 옮기기 (변경 적용 시 회수)">
            전체 회수 ({activeExisting.length})
          </button>
        </div>
      </div>
      <div className={styles.heldBody}>
      {filteredGames.length ? <div className={styles.selectedList}>
        {visibleGames.map((g) => (
          <div key={g.projectId} className={`${styles.gameRow} ${styles.permissionGameRow} ${g.changeKind === 'grant' ? styles.permissionGamePending : ''}`}>
            {/* 보유 행은 체크박스 없이 행 끝 X로만 뺀다 — 추가 목록의 '+'와 짝. */}
            <ProjectThumb prefix={g.prefix} display_name={g.displayName} iconUrl={g.iconUrl} size={48} />
            <div className={styles.gameInfo}>
              <div className={`${styles.gameName} ${styles.gameNameRow}`}>
                <TruncatedName text={g.displayName || g.prefix} />
              </div>
              {g.stage && <div className={styles.gameStageRow}>{stageLabel(g.stage as Stage)}</div>}
            </div>
            <div className={styles.gameRight}>
              {g.changeKind === 'grant' && <span className={`${styles.changeBadge} ${styles.grant}`}>추가 예정</span>}
              {g.changeKind === 'update' && <span className={`${styles.changeBadge} ${styles.update}`}>역할 변경 예정</span>}
              <LevelSegment value={g.level} disabled={disabled}
                groupLabel={`${g.displayName || g.prefix} 역할`}
                onSelect={lv => onSetLevel(g.projectId, lv)} />
              <button type="button" className={styles.removeBtn} onClick={() => onRemove(g.projectId)}
                disabled={disabled} aria-label={`${g.displayName || g.prefix} ${g.changeKind === 'grant' ? '목록에서 선택 해제' : '권한 회수'}`}
                title={g.changeKind === 'grant' ? '선택 해제' : '권한 회수 (추가 목록으로 이동)'}>
                <NavIcon name="x" size={14} color="#9B9B9B" />
              </button>
            </div>
          </div>
        ))}
      </div> : !disabledMessage && <div className={styles.selectedEmpty}>
        {games.length ? '검색 조건에 맞는 권한이 없습니다.' : '왼쪽 목록에서 게임을 넣으면 여기에 표시됩니다.'}
      </div>}
      </div>
      {filteredGames.length > GAME_PAGE_SIZE && <div className={styles.permissionGamePagination}>
        <Pagination page={currentPage} total={filteredGames.length} pageSize={GAME_PAGE_SIZE} onChange={setPage} />
      </div>}
    </section>
  );
}
